/**
 * Paso de suscripción y cobro vía PayPal.
 *
 * Notas técnicas:
 * - Los `planIds` corresponden a planes creados previamente en PayPal.
 * - El `client-id` idealmente debe llegar por `REACT_APP_PAYPAL_CLIENT_ID`.
 * - Tras aprobar la suscripción, el `subscriptionId` se persiste en Firestore.
 */
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore"; // Importar Firestore
import { auth, db } from "../firebase"; // Importar autenticación y Firestore
import "./Payment.scss";
import runtimeConfig from "../config/runtimeConfig";
import { getMembershipPlan } from "./membershipPlanData";

const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedPlan = location.state?.selectedPlan || "Basic";
  const selectedPlanData = getMembershipPlan(selectedPlan);

  // IDs de los planes de suscripción creados en PayPal.
  const planIds = runtimeConfig.paypalPlanIds;

  const handlePaymentSuccess = async (subscriptionId) => {
    try {
      // Obtenemos el UID del usuario actual de Firebase Auth
      const user = auth.currentUser;
      if (user) {
        const renewalDate = new Date();
        renewalDate.setMonth(renewalDate.getMonth() + 1);

        // Actualizamos el documento del usuario en Firestore con el subscriptionId
        const userDocRef = doc(db, "users", user.uid);
        await updateDoc(userDocRef, {
          subscriptionId: subscriptionId, // Guardamos el subscriptionId
          subscriptionPlan: selectedPlanData.key,
          subscriptionCost: selectedPlanData.price,
          subscriptionStartDate: serverTimestamp(),
          subscriptionRenewalDate: renewalDate,
        });

        console.log(
          "Subscription ID actualizado en Firestore:",
          subscriptionId
        );

        // Redirigir al dashboard
        navigate("/dashboard");
      } else {
        console.error("Usuario no autenticado");
      }
    } catch (error) {
      console.error(
        "Error actualizando Firestore con el Subscription ID:",
        error
      );
    }
  };

  return (
    <div className="payment-container">
      <div className="logo-payment">
        <img src="/assets/images/logoBlanco.png" alt="Mentfull Logo" />
      </div>

      <div className="payment-card">
        <span className="payment-kicker">Paso final de suscripción</span>
        <h2>Confirma tu membresía {selectedPlanData.name}</h2>
        <p>
          {selectedPlanData.formattedPrice} MXN al mes. Puedes cambiar el plan
          antes de autorizar el pago.
        </p>
      </div>

      <div className="paypal-button-container">
        <PayPalScriptProvider
          options={{
            "client-id": runtimeConfig.paypalClientId,
            vault: true,
          }}
        >
          <div className="paypal-button-wrapper">
            <PayPalButtons
              style={{ layout: "vertical" }}
              createSubscription={(data, actions) => {
                return actions.subscription.create({
                  plan_id: planIds[selectedPlan],
                });
              }}
              onApprove={(data, actions) => {
                const subscriptionId = data.subscriptionID; // Obtenemos el Subscription ID de PayPal
                console.log("Detalles del pago:", data);
                handlePaymentSuccess(subscriptionId); // Actualizamos Firestore con el subscriptionId
              }}
              onError={(err) => {
                console.error("Error en el proceso de pago:", err);
              }}
            />
          </div>
        </PayPalScriptProvider>
      </div>

      <p className="payment-info">
        Serás redirigido automáticamente a tu dashboard una vez completado el
        pago.
      </p>

      <button className="back-button" onClick={() => navigate("/membresias")}>
        Cambiar membresía
      </button>
    </div>
  );
};

export default Payment;
